import React, {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  type ReactElement,
  useMemo
} from "react";

import { Flex } from "@magnetic/flex";
import { type RowData } from "@tanstack/react-table";

import { SelectedRowsCount } from "./selected-rows-count";
import { type BulkActionBarContextType } from "./types";

/**
 *
 * ------ Cloned code from Magnetic Table since BulkActionBar is not publicly exported ----
 *
 * TODO: figure a smarter way to use vite to copy the source
 */

/* INTERNAL not the same as the exposed `BulkActionBarProps` in index barrel!! */
type BulkActionBarProps<TData extends RowData> = object &
  BulkActionBarContextType<TData>;

/**
 * @private - Do not use this version except for the type data grid component. It needs this only for reusability reasons
 * @deprecated - not for general use
 */
export const BulkActionBar = <TData extends RowData>({
  children,
  selectedRows,
  selectAllRows,
  totalRows
}: PropsWithChildren<BulkActionBarProps<TData>>) => {
  const buttonsWithCallback = useMemo(
    () =>
      Children.map(children, (child) => {
        if (child === undefined) {
          return child;
        }

        if (isValidElement(child) && "onClick" in child.props) {
          const originalCallback = child.props?.onClick;
          return cloneElement(child as ReactElement, {
            onClick: () => originalCallback(selectedRows)
          });
        }
        return child;
      }),
    [children, selectedRows]
  );

  return (
    <Flex align="center" className="mds-rebuild-bulk-action-bar-wrapper">
      <Flex
        align="center"
        className="mds-rebuild-bulk-action-bar"
        justify="space-between"
      >
        <Flex className="mds-rebuild-bulk-action-bar-selection">
          <SelectedRowsCount
            selectAllRowsCallback={selectAllRows}
            selectedRowsLength={selectedRows.length}
            totalRows={totalRows}
          />
        </Flex>
        <Flex className="mds-rebuild-bulk-action-bar-actions" gap={8}>
          {buttonsWithCallback}
        </Flex>
      </Flex>
    </Flex>
  );
};
